import type {
  RecipientResponseState,
  RecipientSendStatus,
  RecipientStatus,
} from "@/api/campaigns.service";
import type { CampaignChipColor } from "./campaign-display";

// Effective recipient status in the projection (arch-discussion §14.7):
// new = no recipient/target yet, scheduled = target of an unfired segment,
// sent = already claimed/dispatched.
export const RECIPIENT_STATUSES: RecipientStatus[] = ["new", "scheduled", "sent"];

/** Maps a recipient effective status onto a theme-var dot color. */
export function recipientStatusColor(status: string): string {
  switch (status) {
    case "sent":
      return "var(--color-success, #16a34a)";
    case "scheduled":
      return "var(--color-primary)";
    case "new":
    default:
      return "var(--color-text-secondary)";
  }
}

/** Capitalizes the first letter of a status value for display. */
export function recipientStatusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// The one send state that is resend-eligible. The operator may only select such a
// row once the segment that CLAIMED it is no longer active (see `isSegmentActive`):
// campaign-api refuses to resend into an in-flight segment and reports those back
// as `skipped`, so the UI locks them up front. The projection `status` can't express
// this — a claimed row always derives `status === "sent"`.
export const RESENDABLE_SEND_STATUS: RecipientSendStatus = "submit_failed";

// ---------------------------------------------------------------------------
// Merged, business-friendly status — ONE value operators see, folding the raw
// send_status and the derived projection status into five buckets. send_status
// takes priority; when absent we fall back to the projection status.
// ---------------------------------------------------------------------------

export type MergedRecipientStatus =
  | "new"
  | "scheduled"
  | "sent"
  | "unconfirmed"
  | "failed"
  | "suppressed";

export const MERGED_STATUSES: MergedRecipientStatus[] = [
  "new",
  "scheduled",
  "sent",
  "unconfirmed",
  "failed",
  "suppressed",
];

// The merged bucket whose selection drives the resend flow.
export const RESENDABLE_MERGED_STATUS: MergedRecipientStatus = "failed";

// Operator hint for the Failed status: campaign-api only sets `submit_failed`
// on PERMANENT SendGrid rejections (bad template, unverified sender, malformed
// payload) — transient 429/5xx are auto-retried and never land here — so a
// blind resend just fails again until the root cause is fixed.
export const FAILED_STATUS_HINT =
  "SendGrid rejected the send (e.g. invalid template or unverified sender). Fix the cause, then resend.";

// Operator hint for the Unconfirmed status: a `submitting` row outside an active
// send means the worker never heard back from SendGrid — the email may or may
// not have gone out, so it is deliberately NEVER auto-retried.
export const UNCONFIRMED_STATUS_HINT =
  "No response from SendGrid (network timeout during send). Delivery unknown — verify in SendGrid Activity before resending.";

// Operator hint for the Sent status: `submitted` only means SendGrid ACCEPTED
// the message (HTTP 202) — it says nothing about actual delivery.
export const SENT_STATUS_HINT = "SendGrid accepted the email (202) — delivery is not confirmed.";

// Segment statuses that mean the claiming send is still ACTIVE, as opposed to
// terminal (`sent`/`canceled`). Mirrors campaign-api's ACTIVE_SEGMENT_STATUSES,
// which backs both the projection's `segment_status` and its `sendActive` filter.
const ACTIVE_SEGMENT_STATUSES = ["draft", "scheduled", "sending"];

// True when the campaign_send_segment that claimed this row is still active, i.e.
// its send is genuinely in progress. A null `segment_status` means no recipient
// row / no segment, so there is no active send either.
export function isSegmentActive(segmentStatus: string | null | undefined): boolean {
  return !!segmentStatus && ACTIVE_SEGMENT_STATUSES.includes(segmentStatus);
}

// Computes the merged status of a projection row. Precedence: real send_status
// wins when present (submitted→Sent, submit_failed→Failed, suppressed→Suppressed,
// pending→"in-flight" Scheduled, submitting→Scheduled while the segment that
// claimed it is still active, Unconfirmed once that segment is terminal or gone);
// otherwise an opted-out / undeliverable email reads as Suppressed (excluded from
// sends); otherwise the projection status decides (scheduled→Scheduled, everything
// else including a stray `sent`→New).
export function mergedStatusOf(row: {
  status: string;
  send_status?: RecipientSendStatus | null;
  segment_status?: string | null;
  opted_out?: boolean;
}): MergedRecipientStatus {
  switch (row.send_status) {
    case "submitted":
      return "sent";
    case "submit_failed":
      return "failed";
    case "suppressed":
      return "suppressed";
    case "pending":
      return "scheduled";
    case "submitting":
      // Normal in-flight while the claiming segment is still active. Once that
      // segment is terminal (or absent) it's a stuck row left behind by a network
      // timeout: delivery is unknown and it is never auto-retried — surface it for
      // a manual check. NOTE: the projection `status` cannot discriminate here — a
      // non-null send_status always derives `status === "sent"`, since both come
      // from the same campaign_recipient row.
      return isSegmentActive(row.segment_status) ? "scheduled" : "unconfirmed";
    default:
      // A whole-email suppression (global/edition opt-out or hard bounce) with no
      // send yet: surface the grey "Suppressed" chip rather than "New".
      if (row.opted_out) return "suppressed";
      return row.status === "scheduled" ? "scheduled" : "new";
  }
}

type MergedStatusChip = { label: string; color: CampaignChipColor; dotColor: string };

// Maps a merged status onto its display chip. Failed reads as a prominent red,
// Unconfirmed as a warning amber (needs a manual check); the rest mirror the
// campaign/segment chip palette (Sent green, Scheduled blue, New/Suppressed
// muted grey).
export function mergedStatusChip(status: MergedRecipientStatus): MergedStatusChip {
  switch (status) {
    case "sent":
      return { label: "Sent", color: "success", dotColor: "var(--color-white)" };
    case "scheduled":
      return { label: "Scheduled", color: "primary", dotColor: "var(--color-primary)" };
    case "unconfirmed":
      return {
        label: "Unconfirmed",
        color: "warning",
        dotColor: "var(--color-warning, #d97706)",
      };
    case "failed":
      return { label: "Failed", color: "error", dotColor: "var(--color-error)" };
    case "suppressed":
      return { label: "Suppressed", color: "default", dotColor: "var(--color-text-secondary)" };
    default:
      return { label: "New", color: "default", dotColor: "var(--color-text-secondary)" };
  }
}

/** Filter-dropdown label for a merged status (chip labels reused). */
export function mergedStatusLabel(status: MergedRecipientStatus): string {
  return mergedStatusChip(status).label;
}

// ---------------------------------------------------------------------------
// Survey-response state — an ORTHOGONAL axis to the send-status merge above.
// Derived from the per-recipient responded_count / total_count the projection
// returns, and rendered as its own column + filter (never folded into the
// status chip). The three buckets mirror the backend `responseState` param.
// ---------------------------------------------------------------------------

export const RECIPIENT_RESPONSE_STATES: RecipientResponseState[] = ["none", "partial", "full"];

// Classifies a row by how many of its survey links have been responded to:
// full = all links answered (total > 0); partial = some but not all; none = zero.
export function responseStateOf(row: {
  responded_count: number;
  total_count: number;
}): RecipientResponseState {
  if (row.total_count > 0 && row.responded_count >= row.total_count) return "full";
  if (row.responded_count > 0) return "partial";
  return "none";
}

type ResponseStateChip = { color: CampaignChipColor; dotColor: string };

// Colour cue for the M/N Response chip (full green, partial amber, none muted).
// The "M/N" text carries the meaning; the colour is a redundant, non-sole cue.
export function responseStateChip(state: RecipientResponseState): ResponseStateChip {
  switch (state) {
    case "full":
      return { color: "success", dotColor: "var(--color-success, #16a34a)" };
    case "partial":
      return { color: "warning", dotColor: "var(--color-warning, #d97706)" };
    default:
      return { color: "default", dotColor: "var(--color-text-secondary)" };
  }
}

// Filter-dropdown label for a response state.
export function responseStateLabel(state: RecipientResponseState): string {
  switch (state) {
    case "full":
      return "Fully responded";
    case "partial":
      return "Partially responded";
    default:
      return "Not responded";
  }
}

// Translates a merged filter value into the matching backend query params:
// New/Scheduled drive the projection `status`; Sent/Unconfirmed/Failed/Suppressed
// drive the real `sendStatus`. Unconfirmed additionally pins `sendActive: false`,
// since `submitting` alone also matches rows that are merely mid-send — only rows
// whose claiming segment is terminal/gone are genuinely stuck. Kept server-side so
// pagination, the total, and "select all matching" all describe the same set.
export function mergedFilterToParams(status: MergedRecipientStatus | undefined): {
  status?: RecipientStatus;
  sendStatus?: RecipientSendStatus;
  sendActive?: boolean;
} {
  switch (status) {
    case "new":
      return { status: "new" };
    case "scheduled":
      return { status: "scheduled" };
    case "sent":
      return { sendStatus: "submitted" };
    case "unconfirmed":
      return { sendStatus: "submitting", sendActive: false };
    case "failed":
      return { sendStatus: "submit_failed" };
    case "suppressed":
      return { sendStatus: "suppressed" };
    default:
      return {};
  }
}
